const baseUrl = process.env.NEXT_PUBLIC_CATALOGS_API_URL;

async function logCatalogAction(action, 
  catalogName, 
  itemId = null, 
  body = null, 
  success = true, 
  errorMessage = null, 
  token = null, 
  organizationId = null, 
  userId = null) {
  try {
    const requestMeta = {
      queryParams: {},
      ipAddress: 'client-side',
      endpoint: `${baseUrl}/${getEndpoint(catalogName)}`,
      method: action === 'create' ? 'POST' : action === 'update' ? 'PUT' : 'DELETE'
    };

    const logData = {
      entityName: catalogName,
      entityId: itemId || null,
      action: action,
      userId: userId, 
      organizationId: organizationId,
      requestBody: body,
      newData: action === 'delete' ?  {body: body} : null,
      success: success,
      errorMessage: errorMessage,
      ...requestMeta
    };

    const logResponse = await fetch(`${baseUrl}/logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'flex-organization': organizationId,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(logData),
    });

    return logResponse.ok;
  } catch (error) {
    console.error('Error logging catalog action:', error);
    return false;
  }
}

function getEndpoint(catalogName) {
  const endpoints = {
    carriers: 'carriers',
    conveyances: 'conveyances',
    equipments: 'equipments',
    drivers: 'drivers',
    passengers: 'passengers',
    parties: 'parties',
    conveyanceTypes: 'conveyance/types',
    equipmentTypes: 'equipment/types',
    makeTypes: 'make/types',
  };

  return endpoints[catalogName] || catalogName;
}

export async function fetchCatalogItems(catalogName, token, organizationId, params = {}, customsBorder = null) {
  let url = new URL(`${baseUrl}/${getEndpoint(catalogName)}`);
  
  if (catalogName === 'equipmentTypes') {

    params = params || {};
    
    if (!customsBorder) {
      params.isCBP = "true";
      params.isCBSA = "false";
    } else {
      customsBorder = customsBorder.trim();

      let customsBordersArray = customsBorder.split(",").map(item => item.trim());

      if (customsBordersArray.includes("CBP")) {
        params.isCBP = "true";  
      } else {
        params.isCBP = "false"; 
      }

      if (customsBordersArray.includes("CBSA")) {
        params.isCBSA = "true";  
      } else {
        params.isCBSA = "false"; 
      }
    }
  }

  if (params) {
    url.search = new URLSearchParams(params);
  }
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'flex-organization': organizationId,
      Authorization: `Bearer ${token}`,
    }
  });
  const data = await response.json();
  return data;
}

export async function createCatalogItem(catalogName, body, token, organizationId, userId, carrierId) {
  const url = new URL(`${baseUrl}/${getEndpoint(catalogName)}`);
  
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'flex-organization': organizationId,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (response.status !== 200) {
      if (response.status) {
        const data = await response.json();

        return {
          success: false,
          ...data,
        };
      } else {
        throw Error(` ${catalogName} item`);
      }
    }
    
    const data = await response.json();

    await logCatalogAction('create', catalogName, data.data.id || data.data._id, body, true, null, token, organizationId, userId);
    
    return data;
  } catch (error) {
    await logCatalogAction('create', catalogName, null, body, false, error.message, token, organizationId, userId);
    throw error;
  }
}

export async function updateCatalogItem(
  catalogName,
  itemId,
  body,
  token,
  organizationId,
  userId,
  signal = null
) {
  try {
    const response = await fetch(`${baseUrl}/${catalogName}/${itemId}`, {
      signal,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'flex-organization': organizationId,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (response.status !== 200) {
      if (response.status) {
        const data = await response.json();
    
        return {
          success: false,
          ...data,
        };
      } else {
       throw Error(`Unable to create ${catalogName} item`);
      }
    }
    
    const data = await response.json();

    await logCatalogAction('update', catalogName, itemId, body, true, null, token, organizationId, userId);
    
    return data;
  } catch (error) {
    await logCatalogAction('update', catalogName, null, body, false, error.message, token, organizationId, userId);
    throw error;
  }
}

export async function removeCatalogItem(catalogName, itemId, token, organizationId, userId, itemData) {
  try {
    const response = await fetch(`${baseUrl}/${catalogName}/${itemId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'flex-organization': organizationId,
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status !== 200) {
     throw Error(`Unable to remove ${catalogName} item`);
    }
    
    const data = await response.json();
    
    await logCatalogAction('delete', catalogName, itemId, itemData, true, null, token, organizationId, userId);
    
    return data;
  } catch (error) {
    await logCatalogAction('delete', catalogName, null, itemData, false, error.message, token, organizationId, userId);
    throw error;
  }
}

export async function fetchCatalogBy(
  token,
  organizationId,
  catalogName,
  filters = {}, // Evita que filters sea undefined
  globalParties = false
) {
  // Asegurar valores por defecto
  const {
    text = '', 
    searchKey = '', 
    searchValue = ''
  } = filters ?? {};  // Evita destructurar undefined


  if(catalogName === 'secondaryCarriers') {
    catalogName = 'carriers';
  }
  const params = {
    text: text || '',  // Convierte `null` en `''`
    customSearchKey: searchKey || '',
    customSearchValue: searchValue || '',
  };


  const paramsFormatted = Object.entries(params).reduce(
    (params, [key, value]) => (value ? `${params}&${key}=${value}` : params),
    ''
  );
  let url = new URL(`${baseUrl}/catalogs/search?type=${catalogName}${paramsFormatted}`);
  if(globalParties){
    url.searchParams.append('globalParties', globalParties);
  }

  const response = await fetch(
   url,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'flex-organization': organizationId,
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const data = await response.json();
  return data;
}

export async function getAllCarriers(token, organizationId, query, params, isActive) {
  let url = new URL(`${baseUrl}/carriers`);
  if (params) {
    url.search = new URLSearchParams(params);
  }
  if (isActive) {
    url.searchParams.append('isActive', isActive);
  }
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'flex-organization': organizationId,
    },
  });

  if (response.status !== 200) {
    throw Error('There was an error fetching all Carriers');
  }

  const result = await response.json();
  return result;
}

export async function getLogs(organizationId, token, query, startDate = null, endDate = null, entityId = null, { pageNumber = null, totalPerPage = null } = {}, searchField = null) {
  let url = new URL(`${baseUrl}/get-logs`);
  if (query) {
    url.searchParams.append('entityName', query);
  }
if (startDate && endDate) {
    url.searchParams.append('startDate', startDate);
    url.searchParams.append('endDate', endDate);
  }
  if (entityId) {
    url.searchParams.append('entityId', entityId);
  }
  if (pageNumber) {
    url.searchParams.append('pageNumber', pageNumber);
  }
  if (totalPerPage) {
    url.searchParams.append('totalPerPage', totalPerPage);
  }
  if( searchField ) {
    url.searchParams.append('searchField', searchField);
  }
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'flex-organization': organizationId,
    },
  });

  if (response.status !== 200) {
    const errorText = await response.text();
    console.error("Error response:", errorText);
    throw Error(`There was an error fetching logs: ${response.status} - ${errorText}`);
  }

  const result = await response.json();
  return result;
}